import { getTeamsNamesByEmails } from '@service/apis';
import { API } from '@service';
import {
  buildDomainCheckPreviewRow,
  buildDomainCheckResponseMap,
  VALIDATION_ERROR,
} from './clientDomainCheck';
import {
  buildPwcEmployeePreviewRow,
  buildPwcEmployeeResponseMap,
  PWC_VALIDATION_ERROR,
} from './pwcEmployeeCheck';

const isEmailOnProject = (email, existingUsers) =>
  existingUsers.some(
    (user) => user?.email && user.email.toLowerCase() === email.toLowerCase(),
  );

const buildPreviewRow = (email, status, reason, extra = {}) => ({
  email,
  status,
  reason,
  details: reason,
  ...extra,
});

const buildInvalidRow = (email, reason) =>
  buildPreviewRow(email, 'invalid', reason);

const buildSkippedRow = (email) =>
  buildPreviewRow(email, 'skipped', 'Already on project');

/**
 * Validate PwC emails against directory search and existing project members.
 */
export const validatePwcEmails = async (emails, existingMembers = []) => {
  const existing = existingMembers.filter((m) => m?.email || m?.guid);
  const skippedEmails = new Set();
  const toValidate = [];

  emails.forEach((email) => {
    if (isEmailOnProject(email, existing)) {
      skippedEmails.add(email.toLowerCase());
    } else {
      toValidate.push(email);
    }
  });

  let responseMap = new Map();

  if (toValidate.length > 0) {
    try {
      const response = await getTeamsNamesByEmails(toValidate);
      responseMap = buildPwcEmployeeResponseMap(response);
    } catch {
      return emails.map((email) =>
        skippedEmails.has(email.toLowerCase())
          ? buildSkippedRow(email)
          : buildInvalidRow(email, PWC_VALIDATION_ERROR),
      );
    }
  }

  return emails.map((email) => {
    if (skippedEmails.has(email.toLowerCase())) {
      return buildSkippedRow(email);
    }

    const item = responseMap.get(email.toLowerCase());
    return buildPwcEmployeePreviewRow(email, item);
  });
};

/**
 * Validate client emails against domain check and existing project clients.
 */
export const validateClientEmails = async (emails, existingClients = []) => {
  const existing = existingClients.filter((c) => c?.email);
  const skippedEmails = new Set();
  const toValidate = [];

  emails.forEach((email) => {
    if (isEmailOnProject(email, existing)) {
      skippedEmails.add(email.toLowerCase());
    } else {
      toValidate.push(email);
    }
  });

  let responseMap = new Map();

  if (toValidate.length > 0) {
    try {
      const response = await API.validateClientEmails({ emails: toValidate });
      responseMap = buildDomainCheckResponseMap(response);
    } catch {
      return emails.map((email) =>
        skippedEmails.has(email.toLowerCase())
          ? buildSkippedRow(email)
          : buildInvalidRow(email, VALIDATION_ERROR),
      );
    }
  }

  return emails.map((email) => {
    if (skippedEmails.has(email.toLowerCase())) {
      return buildSkippedRow(email);
    }

    const item = responseMap.get(email.toLowerCase());
    return buildDomainCheckPreviewRow(email, item);
  });
};

export const buildInvalidFormatRows = (invalidEmails) =>
  invalidEmails.map((email) => buildInvalidRow(email, 'Invalid email format'));
